import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import moment from 'moment-timezone';
import Icon, { StopOutlined, ExclamationCircleOutlined, RedoOutlined } from '@ant-design/icons';

import RecordDetail from './RecordDetail';
import RecordDetailHeader from './RecordDetailHeader';
import './sentRecordStatus.scss';
import { verifyFeatureAccess } from '../../global/featureFlags';
import { captureAnalytics } from '../../actions/preload';
import {
  RESEND_BLOCKED_HINT, canResendFromStatus, openStatusPageResend, retryingLabel,
} from './failedFaxVisibility';

const recordStatus = {
  can_view_and_send: 'View and Send',
  can_view: 'View only',
  revoked: 'Revoked',
};

class SentRecordStatus extends Component {
  constructor(props) {
    super(props);
    this.state = {
      timezone: moment.tz.guess(),
      patientName: props.data.patient_name || 'Not Provided',
      patientDOB: props.data.patient_dob || 'Not Provided',
    };

    this.displayRecords = this.displayRecords.bind(this);
  }

  // ENG-3427: a fax dialed from a fax machine (ATA) is minted with
  // skip_guest_access_code, so there is no secure link and no digital access -- the
  // opposite of what the direct_fax sentence below claims.
  // `status`, not `recordStatus`: that name is the label map declared above this class.
  getRecipientAccess(viewCount, status, isAtaFax) {
    if (isAtaFax) return 'The recipient received this fax at their fax machine. There is no digital copy to share.';
    const sentAsFax = status === 'direct_fax';
    if (sentAsFax) return 'The recipient received this record as a fax and has digital access as well';
    return 'The recipient has digital access to this record';
  }

  getViewCount(viewCount) {
    if (viewCount === 0) return 'This record has not yet been viewed';
    return viewCount;
  }

  /**
   * Opens the modal dialog
   * First, Checks if user can revoke record.
   */
  handleDialogOpen = async () => {
    const { fileToken, openModal } = this.props;
    const canRevoke = await verifyFeatureAccess('revoke_record');
    if (canRevoke) {
      const data = {
        recordId: fileToken,
        // eslint-disable-next-line react/destructuring-assignment
        sendToken: this.props.data.send_token,
      };
      const modalType = {
        type: 'REVOKE_RECORDS',
        data,
      };
      return openModal(modalType);
    }
    const modalType = {
      type: 'RAISE_FLAG',
      featureFlag: 'revoke_record',
    };
    openModal(modalType);
  };

  handleResend = () => {
    const { data, fileToken, openModal } = this.props;
    return openStatusPageResend({
      data, fileToken, openModal, verifyFeatureAccess, captureAnalytics,
    });
  };

  // displays status of record send through fax
  faxRecordDetails = () => {
    const { timezone, patientName, patientDOB } = this.state;
    const { data } = this.props;
    const { resent_at } = data;

    // Format date and add timezone
    const completedAt = data.completed_at
      ? moment.tz(data.completed_at, timezone).format('lll')
      : 'Delivery pending';
    const expiry = data.expiry === null
      ? 'N/A'
      : `Expires ${moment.tz(data.expiry, timezone).format('lll')}`;
    const sentAt = moment.tz(data.date_sent, timezone).format('lll');
    const resentDates = resent_at.map(date => moment.tz(date, timezone).format('lll'));
    const memo = data.memo || 'N/A';
    const pagesSent = data.total_pages_sent ? data.total_pages_sent : 'N/A';
    const numPages = data.num_pages ? data.num_pages : 'N/A';
    let details = [
      // ENG-3679: a resent fax keeps its failed fax_status; say what happened instead,
      // with no status icon (a check would read as delivered).
      data.resent === true
        ? { cellHeader: 'Delivery Status', value: 'Resent. This fax did not go through and was sent again as a new fax, listed separately in Sent.' }
        : { cellHeader: 'Delivery Status', value: retryingLabel(data) || data.fax_status, faxStatus: data.fax_status },
      { cellHeader: 'Name', value: patientName },
      { cellHeader: 'Patient DOB', value: patientDOB },
      { cellHeader: 'Memo', value: memo },
      { cellHeader: 'Access status', value: expiry },
      { cellHeader: 'Delivery Completion Time', value: completedAt },
      { cellHeader: 'Last Sent At', value: sentAt },
      { cellHeader: 'Times Sent', value: resentDates },
      { cellHeader: 'No. pages in original record', value: numPages },
      { cellHeader: 'Pages Sent', value: pagesSent },
    ];

    if (data.fax_error_message) {
      details = [...details, { cellHeader: 'Fax Error', value: data.fax_error_message }];
    }

    return details;
  };

  // displays status of records sent through email
  emailRecordDetails = () => {
    const { timezone, patientName, patientDOB } = this.state;
    const { data } = this.props;
    const sentAt = moment.tz(data.date_sent, timezone).format('lll');
    const { record_status, resent_at } = data;
    const resentDates = resent_at.map(date => moment.tz(date, timezone).format('lll'));
    const memo = data.memo || 'N/A';
    const pagesSent = data.total_pages_sent ? data.total_pages_sent : 'N/A';
    const numPages = data.num_pages ? data.num_pages : 'N/A';
    const details = [
      { cellHeader: 'Record Status', value: recordStatus[record_status] },
      { cellHeader: 'Name', value: patientName },
      { cellHeader: 'Patient DOB', value: patientDOB },
      { cellHeader: 'Memo', value: memo },
      { cellHeader: 'Last Sent At', value: sentAt },
      { cellHeader: 'Times Sent', value: resentDates },
      { cellHeader: 'No. pages in original record', value: numPages },
      { cellHeader: 'Pages Sent', value: pagesSent },
    ];

    return details;
  };

  // Displays all rows in tabular format
  displayRecords() {
    const { fax_status } = this.props.data;
    const statusDetails = fax_status ? this.faxRecordDetails() : this.emailRecordDetails();

    return statusDetails.map((detail) => {
      return (<RecordDetail key={detail.cellHeader} {...detail} />);
    });
  }

  render() {
    const { data, patient } = this.props;
    const isAtaFax = data.is_ata_fax === true;
    const viewCount = this.getViewCount(data.view_count, data.record_status);
    const receiver = data.receiver_name ? `${data.receiver_name} at ${data.receiver}` : data.receiver;
    const recipientAccess = this.getRecipientAccess(data.view_count, data.record_status, isAtaFax);
    return (
      <div>
        <div className="sent-records-header">
          <h1>Sent Record Details</h1>
          {data && (data.record_status === 'revoked')
            && (
              <div className="record-status">
                <Icon component={ExclamationCircleOutlined} style={{ marginTop: '10px' }} />
                <p>The recipient&#39;s access to view this record has been revoked</p>
              </div>
            )
          }
        </div>
        { data
          ? (
            <div className="record-details">
              <RecordDetailHeader title="Recipient" content={receiver} />
              <RecordDetailHeader title="Access" content={recipientAccess} />
              {/* Nothing to view and nothing to revoke when there is no digital copy. */}
              {!isAtaFax && <RecordDetailHeader title="View Count" content={viewCount} />}
              <div className="main-details">
                {this.displayRecords()}
              </div>
              {!isAtaFax && (
                <div className="button-holder">
                  {canResendFromStatus(data, patient) && (
                    <Button
                      type="primary"
                      onClick={this.handleResend}
                      icon={<RedoOutlined />}
                      style={{ marginRight: '10px' }}
                      disabled={data.resend_blocked === true}
                      title={data.resend_blocked === true ? RESEND_BLOCKED_HINT : undefined}
                    >
                      Resend Fax
                    </Button>
                  )}
                  <Button
                    style={{ backgroundColor: 'rgb(255,0,0)', color: 'rgb(255, 255, 255)' }}
                    onClick={() => this.handleDialogOpen()}
                    disabled={data.record_status === 'revoked' || patient}
                    icon={<StopOutlined />}
                  >
                    Revoke Access
                  </Button>
                </div>
              )}
            </div>
          ) : null
        }
      </div>
    );
  }
}

SentRecordStatus.contextTypes = {
  router: PropTypes.object.isRequired,
};

SentRecordStatus.defaultProps = {
  patient: false,
};

SentRecordStatus.propTypes = {
  data: PropTypes.shape({
    fax_status: PropTypes.string,
    fax_error_message: PropTypes.string,
    resent: PropTypes.bool,
    receiver: PropTypes.string.isRequired,
    patient_name: PropTypes.string.isRequired,
    patient_dob: PropTypes.string.isRequired,
    date_sent: PropTypes.string.isRequired,
    resent_at: PropTypes.array.isRequired,
    completed_at: PropTypes.number,
    expiry: PropTypes.string,
    record_status: PropTypes.string.isRequired,
    memo: PropTypes.string,
    total_pages_sent: PropTypes.string,
    num_pages: PropTypes.string,
    is_ata_fax: PropTypes.bool,
    view_count: PropTypes.number,
    fax_retry_count: PropTypes.number,
    fax_resend_available: PropTypes.bool,
    resend_blocked: PropTypes.bool,
    send_token: PropTypes.string,
    receiver_name: PropTypes.string,
  }).isRequired,
  openModal: PropTypes.func.isRequired,
  patient: PropTypes.bool,
  fileToken: PropTypes.string.isRequired,
};

export default SentRecordStatus;
